import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Product }                    from '../types';
import { fetchProduct }               from '../utils/api';
import { WHATSAPP_URL, PLACEHOLDER_IMG, slugifyCategory } from '../utils/constants';
import ProductCard   from '../components/ProductCard';
import SizeRecommender from '../components/SizeRecommender';
import EnquiryForm   from '../components/EnquiryForm';
import SEOHead from '../components/SEOHead';
import './ProductDetail.css';

interface ProductImage { id: number; image_url: string; is_primary: number; }
type Tab = 'enquiry' | 'size';

const ProductDetail: React.FC = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product,    setProduct]    = useState<Product | null>(null);
  const [related,    setRelated]    = useState<Product[]>([]);
  const [images,     setImages]     = useState<ProductImage[]>([]);
  const [activeImg,  setActiveImg]  = useState<string>('');
  const [loading,    setLoading]    = useState<boolean>(true);
  const [tab,        setTab]        = useState<Tab>('enquiry');

  useEffect(() => {
    if (!id) return;
    window.scrollTo(0, 0);
    setLoading(true);
    fetchProduct(Number(id))
      .then(data => {
        setProduct(data.product);
        setRelated(data.related ?? []);
        const imgs: ProductImage[] = data.product.images ?? [];
        setImages(imgs);
        // Set active image — primary first, then first image, then product.image
        const primary = imgs.find(i => i.is_primary) ?? imgs[0];
        setActiveImg(primary?.image_url ?? data.product.image ?? '');
      })
      .catch(() => navigate('/products'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div className="loader" style={{ minHeight: '60vh' }}><div className="spinner" /></div>;
  if (!product) return null;

  const discount = product.mrp > product.price
    ? Math.round((1 - product.price / product.mrp) * 100) : 0;

  const waMsg = `Hi! I'm interested in: *${product.name}* (₹${product.price})\nASIN: ${product.asin}\n${product.amazon_url}`;

  // All images to show — from product_images table or fallback to product.image
  const allImages: ProductImage[] = images.length > 0
    ? images
    : product.image ? [{ id: 0, image_url: product.image, is_primary: 1 }] : [];

  return (
    <div className="product-detail-page">
      {product && (
        <SEOHead
          title={product.seo_title || product.name}
          description={
            product.seo_description ||
            (product.description
              ? product.description.slice(0, 160)
              : `Buy ${product.name} at ₹${product.price}. ${product.category} from Balaji Fashion House. WhatsApp enquiry available.`)
          }
          keywords={
            product.seo_keywords ||
            `${product.name}, ${product.category}, Indian ethnic wear, women fashion, buy online India`
          }
          image={allImages[0]?.image_url ?? product.image}
          url={`/product/${product.id}`}
          type="product"
          price={product.price}
          availability={product.quantity > 0 ? 'InStock' : 'OutOfStock'}
          brand="Balaji Fashion House"
          category={product.category}
          sku={product.sku}
        />
      )}
      <div className="container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/">Home</Link><span>›</span>
          <Link to="/products">Collections</Link><span>›</span>
          <Link to={`/products/${slugifyCategory(product.category)}`}>{product.category}</Link>
          <span>›</span>
          <span className="bc-current">{product.name.slice(0, 40)}...</span>
        </div>

        <div className="detail-grid">
          {/* Image Gallery */}
          <div className="gallery-wrap">
            {/* Main image */}
            <div className="detail-image-wrap">
              {activeImg
                ? <img src={activeImg} alt={product.name}
                       onError={e => { e.currentTarget.src = PLACEHOLDER_IMG; }} />
                : <div className="no-image-placeholder">📷<br />No Image</div>
              }
              {discount > 0 && <div className="detail-badge">{discount}% OFF</div>}
              {product.quantity === 0 && <div className="out-of-stock-banner">Out of Stock</div>}
            </div>

            {/* Thumbnails — show only if more than 1 image */}
            {allImages.length > 1 && (
              <div className="thumbnails" role="group" aria-label="Product images">
                {allImages.map((img, i) => (
                  <button key={img.id || i} type="button"
                          className={`thumb ${activeImg === img.image_url ? 'active' : ''}`}
                          onClick={() => setActiveImg(img.image_url)}
                          aria-label={`View image ${i + 1}`}
                          aria-pressed={activeImg === img.image_url}>
                    <img src={img.image_url} alt=""
                         loading="lazy"
                         onError={e => { e.currentTarget.src = PLACEHOLDER_IMG; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="detail-info">
            <span className="badge badge-category">{product.category}</span>
            <h1 className="detail-name">{product.name}</h1>

            <div className="detail-price-row">
              <span className="detail-price">₹{product.price.toLocaleString('en-IN')}</span>
              {product.mrp > product.price && (
                <>
                  <span className="detail-mrp">₹{product.mrp.toLocaleString('en-IN')}</span>
                  <span className="detail-discount">Save {discount}%</span>
                </>
              )}
            </div>

            <div className="detail-meta">
              <div><b>SKU:</b> {product.sku}</div>
              <div><b>ASIN:</b> <a href={product.amazon_url} target="_blank" rel="noreferrer">{product.asin}</a></div>
              <div><b>Status:</b>{' '}
                <span className={`badge ${product.quantity > 0 ? 'badge-instock' : 'badge-outstock'}`}>
                  {product.quantity > 0 ? `✅ In Stock (${product.quantity})` : '❌ Out of Stock'}
                </span>
              </div>
            </div>

            <div className="detail-desc">
              <h3>Description</h3>
              <p>{product.description || 'No description available.'}</p>
            </div>

            <a href={WHATSAPP_URL(waMsg)} target="_blank" rel="noreferrer" className="btn-whatsapp btn-lg btn-block">
              💬 Enquire on WhatsApp
            </a>

            {/* Temporarily hidden — uncomment to re-enable the "buy on Amazon" link.
            <a href={product.amazon_url} target="_blank" rel="noreferrer" className="amazon-link-btn">
              🛒 Also available on Amazon India
            </a>
            */}

            <div className="detail-tabs">
              <button className={`tab-btn ${tab === 'enquiry' ? 'active' : ''}`} onClick={() => setTab('enquiry')}>
                📋 Send Enquiry
              </button>
              <button className={`tab-btn ${tab === 'size' ? 'active' : ''}`} onClick={() => setTab('size')}>
                📏 Size Guide
              </button>
            </div>
            {tab === 'enquiry' && <EnquiryForm product={product} />}
            {tab === 'size'    && <SizeRecommender product={product} />}
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="related-section">
            <h2 className="section-title">You May Also Like</h2>
            <div className="section-divider" />
            <div className="products-grid">
              {related.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
